import { Plugin } from 'ckeditor5/src/core';
import { ClickObserver } from 'ckeditor5/src/engine';
import { clickOutsideHandler, ContextualBalloon, ButtonView } from 'ckeditor5/src/ui';

import {
	COMMANDS,
	COMPONENT_NAME
} from '../constants';
import { getRandomID } from '../utils';

import inputIcon from '../../theme/icons/field.svg';

import EditableFieldFormView from './EditableFieldFormView';

const VISUAL_SELECTION_MARKER_NAME = 'editable-field-ui';

export default class EditableFieldUI extends Plugin {
	/**
	 *
	 * @returns {string}
	 */
	static get pluginName() {
		return 'EditableFieldUI';
	}

	static get requires() {
		return [ ContextualBalloon ];
	}

	init() {
		const editor = this.editor;

		editor.editing.view.addObserver( ClickObserver );

		this.formView = this._createFormView();

		this._balloon = editor.plugins.get( ContextualBalloon );

		this._createToolbarButton();

		this._enableUserBalloonInteractions();

		editor.conversion.for( 'editingDowncast' ).markerToHighlight( {
			model: VISUAL_SELECTION_MARKER_NAME,
			view: {
				classes: [ 'ck-fake-editable-field-selection' ]
			}
		} );

		editor.conversion.for( 'editingDowncast' ).markerToElement( {
			model: VISUAL_SELECTION_MARKER_NAME,
			view: {
				name: 'span',
				classes: [ 'ck-fake-editable-field-selection', 'ck-fake-editable-field-selection_collapsed' ]
			}
		} );
	}

	destroy() {
		super.destroy();

		// Destroy created UI components as they are not automatically destroyed (see ckeditor5#1341).
		this.formView.destroy();
	}

	_createFormView() {
		const editor = this.editor;

		const formView = new EditableFieldFormView( editor.locale );

		const createCommand = editor.commands.get( COMMANDS.CREATE );

		formView.inputView.bind( 'isReadOnly' ).to( createCommand, 'isEnabled', value => !value );
		formView.saveButtonView.bind( 'isEnabled' ).to( createCommand );

		this.listenTo( formView, 'submit', () => {
			const { value } = formView.inputView.fieldView.element;
			if ( value ) {
				this._closeFormView();
				const id = getRandomID();
				editor.execute( COMMANDS.CREATE, { value, id } );
				editor.editing.view.focus();
			} else {
				this.formView.inputView.fieldView.hasError = true;

				setTimeout(
					() => ( this.formView.inputView.fieldView.hasError = false ),
					3000
				);
			}
		} );

		this.listenTo( formView, 'cancel', () => {
			this._closeFormView();
		} );

		formView.keystrokes.set( 'Esc', ( data, cancel ) => {
			this._closeFormView();
			cancel();
		} );

		return formView;
	}

	_createToolbarButton() {
		const editor = this.editor;
		const t = editor.t;

		editor.ui.componentFactory.add( COMPONENT_NAME, locale => {
			const buttonView = new ButtonView( locale );

			buttonView.set( {
				label: t( 'Insert field' ),
				icon: inputIcon,
				tooltip: true,
				withText: false
			} );

			const command = editor.commands.get( COMMANDS.CREATE );
			buttonView.bind( 'isEnabled' ).to( command, 'isEnabled' );
			buttonView.bind( 'isOn' ).to( command, 'value', value => !!value );

			this.listenTo( buttonView, 'execute', () => this._showUI() );

			return buttonView;
		} );
	}

	_enableUserBalloonInteractions() {
		this.editor.keystrokes.set( 'Esc', ( data, cancel ) => {
			if ( this._isUIVisible ) {
				this._hideUI();
				cancel();
			}
		} );

		clickOutsideHandler( {
			emitter: this.formView,
			activator: () => this._isUIInPanel,
			contextElements: [ this._balloon.view.element ],
			callback: () => this._hideUI()
		} );
	}

	_addFormView() {
		if ( this._isFormInPanel ) {
			return;
		}

		this.formView.disableCssTransitions();

		this._balloon.add( {
			view: this.formView,
			position: this._getBalloonPositionData()
		} );

		if ( this._balloon.visibleView === this.formView ) {
			this.formView.inputView.fieldView.select();
		}

		this.formView.enableCssTransitions();

		this.formView.inputView.fieldView.element.value = '';
	}

	_closeFormView() {
		this._hideUI();
	}

	_removeFormView() {
		if ( this._isFormInPanel ) {
			this.formView.saveButtonView.focus();

			this._balloon.remove( this.formView );

			this.editor.editing.view.focus();

			this._hideFakeVisualSelection();

			this.formView.inputView.fieldView.hasError = false;
		}
	}

	_showUI() {
		this._showFakeVisualSelection();

		this._addFormView();

		this._startUpdatingUI();
	}

	_hideUI() {
		if ( !this._isUIInPanel ) {
			return;
		}

		const editor = this.editor;

		this.stopListening( editor.ui, 'update' );
		this.stopListening( this._balloon, 'change:visibleView' );

		editor.editing.view.focus();

		this._removeFormView();

		this._hideFakeVisualSelection();
	}

	_startUpdatingUI() {
		const editor = this.editor;
		const viewDocument = editor.editing.view.document;

		let prevSelectionParent = getSelectionParent();

		const update = () => {
			const selectionParent = getSelectionParent();

			if ( selectionParent !== prevSelectionParent ) {
				this._hideUI();
			} else if ( this._isUIVisible ) {
				this._balloon.updatePosition( this._getBalloonPositionData() );
			}

			prevSelectionParent = selectionParent;
		};

		function getSelectionParent() {
			return viewDocument.selection.focus
				.getAncestors()
				.reverse()
				.find( node => node.is( 'element' ) );
		}

		this.listenTo( editor.ui, 'update', update );
		this.listenTo( this._balloon, 'change:visibleView', update );
	}

	get _isFormInPanel() {
		return this._balloon.hasView( this.formView );
	}

	get _isUIInPanel() {
		return this._isFormInPanel || this._areActionsInPanel;
	}

	get _isUIVisible() {
		return this._balloon.visibleView === this.formView || this._areActionsVisible;
	}

	_getBalloonPositionData() {
		const view = this.editor.editing.view;
		const model = this.editor.model;
		const viewDocument = view.document;
		let target;

		if ( model.markers.has( VISUAL_SELECTION_MARKER_NAME ) ) {
			const markerViewElements = Array.from(
				this.editor.editing.mapper.markerNameToElements( VISUAL_SELECTION_MARKER_NAME )
			);
			const newRange = view.createRange(
				view.createPositionBefore( markerViewElements[ 0 ] ),
				view.createPositionAfter( markerViewElements[ markerViewElements.length - 1 ] )
			);

			target = view.domConverter.viewRangeToDom( newRange );
		} else {
			const range = viewDocument.selection.getFirstRange();

			target = view.domConverter.viewRangeToDom( range );
		}

		return { target };
	}

	_showFakeVisualSelection() {
		const model = this.editor.model;

		model.change( writer => {
			const range = model.document.selection.getFirstRange();

			if ( model.markers.has( VISUAL_SELECTION_MARKER_NAME ) ) {
				writer.updateMarker( VISUAL_SELECTION_MARKER_NAME, { range } );
			} else {
				if ( range.start.isAtEnd ) {
					const startPosition = range.start.getLastMatchingPosition(
						( { item } ) => !model.schema.isContent( item ),
						{ boundaries: range }
					);

					writer.addMarker( VISUAL_SELECTION_MARKER_NAME, {
						usingOperation: false,
						affectsData: false,
						range: writer.createRange( startPosition, range.end )
					} );
				} else {
					writer.addMarker( VISUAL_SELECTION_MARKER_NAME, {
						usingOperation: false,
						affectsData: false,
						range
					} );
				}
			}
		} );
	}

	_hideFakeVisualSelection() {
		const model = this.editor.model;

		if ( model.markers.has( VISUAL_SELECTION_MARKER_NAME ) ) {
			model.change( writer => {
				writer.removeMarker( VISUAL_SELECTION_MARKER_NAME );
			} );
		}
	}
}
